import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../../modules/Login/AuthContext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// Tiempo total de inactividad: 15 minutos (900 segundos)
const SESSION_TIMEOUT_SECONDS = 15 * 60;
// Aviso cuando falten 2 minutos (120 segundos)
const WARNING_THRESHOLD_SECONDS = 2 * 60;

export default function SessionGuard() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [currentTime, setCurrentTime] = useState('');
  const [secondsRemaining, setSecondsRemaining] = useState(SESSION_TIMEOUT_SECONDS);
  const [warningOpen, setWarningOpen] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const timerIntervalRef = useRef(null);
  const clockIntervalRef = useRef(null);

  // 🕒 1. Actualizar Reloj en Tiempo Real
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      setCurrentTime(timeStr);
    };

    updateClock();
    clockIntervalRef.current = setInterval(updateClock, 1000);

    return () => {
      if (clockIntervalRef.current) clearInterval(clockIntervalRef.current);
    };
  }, []);

  // 🔄 2. Resetear el temporizador al detectar actividad
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSecondsRemaining(SESSION_TIMEOUT_SECONDS);
    if (warningOpen) {
      setWarningOpen(false);
    }
  }, [warningOpen]);

  // 👂 3. Listeners de eventos de actividad del usuario
  useEffect(() => {
    if (!isAuthenticated) return;

    let throttleTimeout = null;
    const handleUserActivity = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 2000); // Throttle de 2 seg para máximo rendimiento
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true }));

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleUserActivity));
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [isAuthenticated, resetTimer]);

  // ⏳ 4. Cuenta regresiva de sesión
  useEffect(() => {
    if (!isAuthenticated) return;

    timerIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, SESSION_TIMEOUT_SECONDS - elapsed);
      setSecondsRemaining(remaining);

      // Abrir aviso si quedan menos de 2 minutos
      if (remaining <= WARNING_THRESHOLD_SECONDS && remaining > 0) {
        setWarningOpen(true);
      }

      // Si llega a 0 -> Cierre de sesión por inactividad
      if (remaining === 0) {
        clearInterval(timerIntervalRef.current);
        setWarningOpen(false);
        logout();

        Swal.fire({
          icon: 'warning',
          title: 'Sesión Expirada',
          text: 'Tu sesión se ha cerrado automáticamente por inactividad para proteger la seguridad de la empresa.',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#0655A5',
          allowOutsideClick: false,
        }).then(() => {
          navigate('/', { replace: true });
        });
      }
    }, 1000);

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isAuthenticated, logout, navigate]);

  // Formato mm:ss
  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isLowTime = secondsRemaining <= WARNING_THRESHOLD_SECONDS;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
        }}
      >
        {/* Contador de Inactividad con Hora en Tooltip (Opción 3) */}
        <Tooltip
          title={
            <Box sx={{ p: 0.2, textAlign: 'center' }}>
              <Box sx={{ fontWeight: 700, mb: 0.2 }}>⏱️ Tiempo de sesión: {formatCountdown(secondsRemaining)}</Box>
              <Box sx={{ fontSize: '0.7rem', opacity: 0.85 }}>🕒 Hora del sistema: {currentTime || '--:--'}</Box>
            </Box>
          }
          arrow
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              px: 0.6,
              py: 0.15,
              height: 23,
              borderRadius: '5px',
              bgcolor: isLowTime ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${isLowTime ? '#fca5a5' : '#bbf7d0'}`,
              color: isLowTime ? '#dc2626' : '#15803d',
              fontSize: '0.69rem',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              cursor: 'default',
              transition: 'all 0.2s ease',
            }}
          >
            <TimerIcon
              sx={{
                fontSize: '13px',
                color: isLowTime ? '#dc2626' : '#16a34a',
                animation: isLowTime ? 'pulse 1s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                }
              }}
            />
            <span>{formatCountdown(secondsRemaining)}</span>
          </Box>
        </Tooltip>
      </Box>

      {/* Modal de Advertencia de Inactividad */}
      <Dialog
        open={warningOpen}
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1.5,
            maxWidth: '380px',
            textAlign: 'center',
            border: '2px solid #ef4444',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 44, color: '#ef4444' }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            ¿Sigues ahí?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
            Por seguridad, tu sesión se cerrará automáticamente por inactividad en:
          </Typography>
          <Typography
            sx={{
              fontSize: '1.75rem',
              fontWeight: 900,
              fontFamily: 'monospace',
              color: '#dc2626',
              bgcolor: '#fee2e2',
              py: 0.75,
              borderRadius: '10px',
              letterSpacing: '0.05em',
            }}
          >
            {formatCountdown(secondsRemaining)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 1 }}>
          <Button
            variant="contained"
            onClick={resetTimer}
            sx={{
              bgcolor: '#0655A5',
              borderRadius: '10px',
              fontWeight: 700,
              px: 3,
              textTransform: 'none',
              '&:hover': { bgcolor: '#054487' },
            }}
          >
            Mantener sesión activa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
